import { useMemo } from "react";
import { useAppSelector } from "@store/hooks";

// Define the keys for the side tabs in the Service Agreement Policy UI.
export type KeysForBillingSideTabs = "CONTACT_SIDE_TAB" | "ADDRESS_SIDE_TAB" | "BILLING_METHOD_SIDE_TAB";

// Type definition for the indexes of the side tabs.
type SideTabsIndexes = {
    [key in KeysForBillingSideTabs]: number;
};

// Type definition for the state (validity) of each side tab.
type SideTabsStates = {
    [key in KeysForBillingSideTabs]: boolean;
};

/**
 * Custom hook for managing the state and behavior of side tabs in the Service Agreement Billing UI.
 * This hook encapsulates logic for determining the current active tab, its validity, and handling specific
 * UI logic based on the type of service agreement.
 *
 * @returns {Object} An object containing various states and configurations related to the side tabs.
 */
export const useCarePlanBillingSideTab = () => {
    // State selectors from the Redux store.
    const serviceAgreementBillingTab = useAppSelector(({ ui }) => ui.CarePlanBillingActiveSideTab)
    const asContact = useAppSelector(({ ui }) => ui.CarePlanBillingContactAsContact)
    const isContactValid = useAppSelector(({ state }) => state.CarePlanBillingContactValid)
    const isAddressValid = useAppSelector(({ state }) => state.CarePlanBillingAddressValid)
    const isPaymentValid = useAppSelector(({ state }) => state.CarePlanBillingMethodPaymentTypeValid)

    // useMemo to optimize the computation of the state (validity) of each side tab.
    const sideTabsStates = useMemo<SideTabsStates>(() => ({
        CONTACT_SIDE_TAB: isContactValid || asContact,
        ADDRESS_SIDE_TAB: isAddressValid,
        BILLING_METHOD_SIDE_TAB: isPaymentValid
    }), [isContactValid, asContact, isAddressValid, isPaymentValid]);

    // useMemo to optimize the computation of the side tabs' indexes.
    const sideTabsIndexes = useMemo<SideTabsIndexes>(() => ({
        CONTACT_SIDE_TAB: 0,
        ADDRESS_SIDE_TAB: 1,
        BILLING_METHOD_SIDE_TAB: 2
    }), []);

    // Return the computed states and configurations.
    return { serviceAgreementBillingTab, sideTabsStates, sideTabsIndexes };
};
